import {Button, LinearProgress, Link, Typography} from '@material-ui/core';
import React, {ReactElement, useState} from 'react';
import {Controller, FieldErrors, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import LogoIcon from '../login/LogoIcon';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {FormField} from '@components/system/FormField/FormField';
import '../login/Login.scss';
import {useStyles} from '../login/Login';
import {resendOTP, verifyOTP} from '@shared/services/PublicService';
import InitSharedRepo from '../../../../../shared';
import AuthUtils from 'src/utils/AuthUtils';
import {SYSTEM_MODULES} from '@shared/constants/Mapping';
import {State} from '@shared/store/reducers/Index';
import {useDispatch, useSelector} from 'react-redux';
import {
  _login,
  _setDefaultTabAfterAuth,
  _setDefaultGroupAfterAuth,
  _showMessageAlert
} from '@shared/store/actions/AuthActions';
import {BASE_ROUTE_ADMIN} from 'src/constants/route/BaseRoute';
import NavRoutes from 'src/constants/route/NavRoutes';
import Loader from '@components/framework/loader/Loader';
import {consumePostLoginRedirect} from 'src/utils/PostLoginRedirect';
import './VerifyOTP.scss';

interface IVerifyOTPFields {
  otp: string;
}

const VerifyOTP = (props: any): ReactElement => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const classes = useStyles();
  const {
    control,
    formState: {isSubmitting},
    handleSubmit,
    errors,
    setValue
  } = useForm<IVerifyOTPFields>({
    defaultValues: {
      otp: ''
    },
    resolver: values => {
      // eslint-disable-next-line no-shadow
      const errors: FieldErrors<IVerifyOTPFields> = {};
      if (!values.otp || !values.otp.trim()) {
        errors.otp = {
          type: 'required',
          message: t('VERIFY_OTP.VALIDATION.OTP_REQUIRED')
        };
      }
      if (Object.keys(errors).length) {
        return {
          values: {},
          errors
        };
      }
      return {
        values,
        errors: {}
      };
    }
  });
  const [isResendingOTP, setIsResendingOTP] = useState(false);

  const defaultTabAfterAuth = useSelector(
    (state: State) => state.auth.defaultTabAfterAuth
  );

  const verifyOTPUser = useSelector(
    (state: State) => state.auth.verifyOTPUserDetails
  );

  const dispatch = useDispatch();

  const handleAuthRedirect = (token: any, type: string): void => {
    if (token) {
      InitSharedRepo.initAfterAuth({token});
      AuthUtils.setAdminAuthToken({token, portal: BASE_ROUTE_ADMIN});
      const postLoginRedirect = consumePostLoginRedirect();
      if (postLoginRedirect?.route) {
        if (postLoginRedirect.deepLink?.clinic_group_id) {
          dispatch(
            _setDefaultGroupAfterAuth({
              clinic_group_id: postLoginRedirect.deepLink.clinic_group_id
            })
          );
        }
        dispatch(_login(token, null, postLoginRedirect.route));
      } else if (defaultTabAfterAuth === SYSTEM_MODULES.PRIOR_AUTHORIZATIONS) {
        dispatch(
          _setDefaultTabAfterAuth({
            tab: null
          })
        );
        const BASE_ROUTE = `${BASE_ROUTE_ADMIN}/${NavRoutes.PRIVATE.HOME}`;
        const priorAuthURL = `${BASE_ROUTE}/${NavRoutes.PRIVATE.ADMIN.PRIOR_AUTHORIZATIONS}`;
        dispatch(_login(token, null, priorAuthURL));
      } else {
        dispatch(_login(token, null, BASE_ROUTE_ADMIN));
      }
      dispatch(_showMessageAlert(true));
    }
  };

  const handleVerifyOTP = async (values: IVerifyOTPFields) => {
    try {
      const res = await verifyOTP({
        otp: values?.otp
      });
      if (res?.token) {
        handleAuthRedirect(res.token, '');
      }
    } catch (e) {
      if (e?.message) {
        enqueueSnackbar(e.message, SnackBarConfig('e'));
      }
    }
  };

  const handleResendOTP = async () => {
    try {
      setIsResendingOTP(true);
      const res = await resendOTP();
      if (res?.message) {
        enqueueSnackbar(res.message, SnackBarConfig('s'));
      }
      setValue('otp', '');
      setIsResendingOTP(false);
    } catch (e) {
      setIsResendingOTP(false);
      if (e?.message) {
        enqueueSnackbar(e.message, SnackBarConfig('e'));
      }
    }
  };

  return (
    <div
      className={['login-wrapper center-children', 'verify-otp-wrapper'].join(
        ' '
      )}
      data-test-id="pcl-login-loginPage">
      {isSubmitting && (
        <LinearProgress className={classes.loader} color="primary" />
      )}
      <div className="login-content-wrap">
        <LogoIcon />
        <div className={classes.signInBox}>
          <div className={classes.logo}>
            <MedopsLogo style={{height: '100%', width: '30%'}} />
          </div>
          <div className="card-wrapper">
            <div className="card-content">
              <div className="login-form">
                <Typography className="login-title input-header" variant={'h3'}>
                  {t('VERIFY_OTP.TITLE')}
                </Typography>
                <Typography
                  className="login-subtitle input-header otp-sub-title"
                  variant={'subtitle1'}>
                  {t('VERIFY_OTP.SUB_TITLE', {
                    email: verifyOTPUser?.masked_email ?? ''
                  })}
                </Typography>
                <form onSubmit={handleSubmit(handleVerifyOTP)}>
                  <div style={{marginBottom: '0.5rem', fontWeight: 'bold'}}>
                    {t('VERIFY_OTP.OTP_INPUT_LABEL')}
                  </div>
                  <Controller
                    control={control}
                    name={'otp'}
                    render={inputProps => {
                      return (
                        <FormField
                          type="text"
                          {...inputProps}
                          error={errors['otp']}
                          placeholder={t('VERIFY_OTP.OTP_INPUT_PLACEHOLDER')}
                        />
                      );
                    }}
                  />
                  <div className="forgot-p-link" style={{marginTop: '0.5rem'}}>
                    <Typography className="forgot" variant={'subtitle1'}>
                      <Link
                        disabled={isResendingOTP}
                        type={'button'}
                        component={'button'}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}
                        onClick={handleResendOTP}>
                        {isResendingOTP && <Loader size={12} />}
                        {t('VERIFY_OTP.RESEND_OTP_LINK_TEXT') + '?'}
                      </Link>
                    </Typography>
                  </div>
                  <Button
                    fullWidth
                    variant="contained"
                    size={'medium'}
                    data-test-id="pcl-login-signInButton"
                    color="primary"
                    className={[classes.loginButton].join(' ')}
                    type={'submit'}
                    disabled={isSubmitting}
                    onClick={handleSubmit(handleVerifyOTP)}>
                    {t('COMMON.SUBMIT')}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyOTP;
